import { useRef, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Bold, Italic, Underline, List, ListOrdered, Heading2, Heading3, Copy, FileDown, Printer, FileType } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const exportStyles = `body{font-family:'Times New Roman',serif;font-size:12pt;line-height:1.5;color:#000;max-width:17cm;margin:0 auto;padding:1cm}h2{font-size:16pt;text-align:center;margin-bottom:16pt}h3{font-size:13pt;margin-top:14pt}table{border-collapse:collapse;width:100%;margin:8pt 0}th,td{border:1px solid #000;padding:6pt;text-align:left;font-size:11pt}th{background:#f0f0f0}`;

export const DocumentEditor = ({ html, demo, docTitle, color }) => {
  const ref = useRef(null);
  const [stats, setStats] = useState({ words: 0, chars: 0 });

  const updateStats = useCallback(() => {
    const text = ref.current?.innerText || "";
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    setStats({ words, chars: text.length });
  }, []);

  useEffect(() => {
    if (ref.current) {
      ref.current.innerHTML = html;
      updateStats();
    }
  }, [html, updateStats]);

  const cmd = (command, value = null) => {
    ref.current?.focus();
    document.execCommand(command, false, value);
  };

  const fullHtml = () =>
    `<html><head><meta charset="utf-8"><title>${docTitle}</title><style>${exportStyles}</style></head><body>${ref.current?.innerHTML || ""}</body></html>`;

  const copyText = async () => {
    try {
      const htmlContent = ref.current?.innerHTML || "";
      const textContent = ref.current?.innerText || "";
      await navigator.clipboard.write([
        new window.ClipboardItem({
          "text/html": new Blob([htmlContent], { type: "text/html" }),
          "text/plain": new Blob([textContent], { type: "text/plain" }),
        }),
      ]);
      toast.success("Dokument kopiran u međuspremnik");
    } catch {
      try {
        await navigator.clipboard.writeText(ref.current?.innerText || "");
        toast.success("Tekst kopiran u međuspremnik");
      } catch {
        toast.error("Kopiranje nije uspjelo");
      }
    }
  };

  const downloadWord = async () => {
    try {
      const res = await axios.post(`${API}/export/docx`, { html: ref.current?.innerHTML || "" }, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = "dokument.docx";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Word dokument preuzet");
    } catch {
      toast.error("Preuzimanje Word dokumenta nije uspjelo");
    }
  };

  const downloadPdf = async () => {
    toast.info("Pripremam PDF...");
    const html2pdf = (await import("html2pdf.js")).default;
    const wrapper = document.createElement("div");
    wrapper.innerHTML = `<style>${exportStyles}</style><div style="font-family:'Times New Roman',serif;font-size:12pt;line-height:1.5;padding:24px">${ref.current?.innerHTML || ""}</div>`;
    await html2pdf().set({
      margin: 10,
      filename: "dokument.pdf",
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "mm", format: "a4" },
      pagebreak: { mode: ["avoid-all", "css"] },
    }).from(wrapper).save();
    toast.success("PDF preuzet");
  };

  const printDoc = () => {
    const w = window.open("", "_blank");
    if (!w) {
      toast.error("Preglednik je blokirao skočni prozor. Dopustite skočne prozore za ispis.");
      return;
    }
    w.document.write(fullHtml());
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div className="doc-editor" data-testid="document-editor" style={{ borderTopColor: color }}>
      {demo && (
        <div className="demo-banner" data-testid="demo-banner">
          Prikazan je ogledni dokument — AI generiranje aktivira se postavljanjem GEMINI_API_KEY varijable pri deployu.
        </div>
      )}
      <div className="editor-toolbar">
        <div className="flex items-center gap-1 flex-wrap">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => cmd("bold")} data-testid="toolbar-bold" title="Podebljano"><Bold className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => cmd("italic")} data-testid="toolbar-italic" title="Kurziv"><Italic className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => cmd("underline")} data-testid="toolbar-underline" title="Podcrtano"><Underline className="w-4 h-4" /></Button>
          <Separator orientation="vertical" className="h-5 mx-1" />
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => cmd("formatBlock", "h2")} data-testid="toolbar-h2" title="Naslov"><Heading2 className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => cmd("formatBlock", "h3")} data-testid="toolbar-h3" title="Podnaslov"><Heading3 className="w-4 h-4" /></Button>
          <Separator orientation="vertical" className="h-5 mx-1" />
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => cmd("insertUnorderedList")} data-testid="toolbar-ul" title="Lista"><List className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => cmd("insertOrderedList")} data-testid="toolbar-ol" title="Numerirana lista"><ListOrdered className="w-4 h-4" /></Button>
        </div>
        <span className="text-xs text-slate-400 whitespace-nowrap" data-testid="word-count">
          {stats.words} riječi · {stats.chars} znakova
        </span>
      </div>

      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={updateStats}
        className="editor-canvas"
        data-testid="editor-canvas"
      />

      <div className="editor-actions">
        <Button variant="outline" onClick={copyText} data-testid="copy-btn">
          <Copy className="w-4 h-4 mr-2" /> Kopiraj
        </Button>
        <Button onClick={downloadWord} data-testid="download-word-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
          <FileType className="w-4 h-4 mr-2" /> Word (.docx)
        </Button>
        <Button onClick={downloadPdf} data-testid="download-pdf-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
          <FileDown className="w-4 h-4 mr-2" /> PDF
        </Button>
        <Button variant="outline" onClick={printDoc} data-testid="print-btn">
          <Printer className="w-4 h-4 mr-2" /> Ispiši
        </Button>
      </div>
    </div>
  );
};
